import { useMemo } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import type { ReportFormData, FormSection } from '../reportCreation.types';
import { getConditionalDependencyNames } from '../utils/conditionalVisibility.utils';
import {
  buildFieldValueMap,
  getRequiredFieldIds,
  hasMissingRequiredFields,
} from '../utils/missingRequiredFields.utils';

interface UseMissingRequiredIndicatorOptions {
  sections: FormSection[];
  control: Control<ReportFormData>;
  enabled?: boolean;
}

export const useMissingRequiredIndicator = ({
  sections,
  control,
  enabled = true,
}: UseMissingRequiredIndicatorOptions): boolean => {
  const dependencyNames = useMemo(
    () => getConditionalDependencyNames(sections),
    [sections],
  );

  const requiredFieldIds = useMemo(
    () => getRequiredFieldIds(sections),
    [sections],
  );

  const watchNames = useMemo(
    () => Array.from(new Set([...dependencyNames, ...requiredFieldIds])),
    [dependencyNames, requiredFieldIds],
  );

  const watchedValues = useWatch({
    control,
    name: watchNames,
  });

  const valueMap = useMemo(
    () => buildFieldValueMap(watchNames, watchedValues),
    [watchNames, watchedValues],
  );

  return useMemo(() => {
    if (!enabled || !sections.length) {
      return false;
    }

    return hasMissingRequiredFields(sections, (fieldId) => valueMap[fieldId]);
  }, [enabled, sections, valueMap]);
};
